import { useCallback, useEffect, useState } from 'react';
import { Card, StatusBadge } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi, type ApiRequestLogDetail } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

/**
 * 30 הבקשות האחרונות ל-API הציבורי — כולל כשלים — בתוך מסך /super/api.
 *
 * 07/09/2026: הלוג המלא ישב רק ב-/super/audit, ומי שבדק מפתח של לקוח
 * ("kliento", שימוש אחרון היום) לא ראה שם שכל הבקשות שלו נכשלו ב-302 ולא
 * הכניסו ליד. כאן: הרשימה ליד המפתחות, סינון "רק כשלים", וסינון לפי מפתח
 * (מהכפתור "בקשות" בשורת המפתח — לפי id, לא לפי שם, כי שם אינו ייחודי).
 */

export interface ApiLogRow {
  id: number;
  tenant_id: number | null;
  api_key_id: number | null;
  tenant_name: string | null;
  key_name: string | null;
  method: string;
  path: string;
  status: number;
  ip: string | null;
  duration_ms: number | null;
  error: string | null;
  created_at: string;
}

export const RECENT_REQUESTS_LIMIT = 30;
const REFRESH_MS = 15000;

const fmtTime = (s: string) => new Date(s).toLocaleString('he-IL', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

export function RecentApiRequests({ keyId, keyLabel, onClearKey }: {
  /** סינון לפי מפתח — null = כל המפתחות. */
  keyId: number | null;
  keyLabel?: string | null;
  onClearKey: () => void;
}) {
  const [rows, setRows] = useState<ApiLogRow[]>([]);
  // השורה הפתוחה: לחיצה טוענת את הבקשה המלאה (כותרות/גוף/תשובה) + אבחון.
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ApiRequestLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superApi.apiRequestLogs({
        limit: RECENT_REQUESTS_LIMIT,
        ...(keyId ? { api_key_id: keyId } : {}),
        ...(failuresOnly ? { status: 'error' } : {}),
      });
      setRows((res.data ?? []) as ApiLogRow[]);
      setTotal(res.meta?.total ?? 0);
    } catch {
      // הלוג הוא תצוגה משנית — כשל טעינה לא מפיל את מסך ה-API.
    } finally {
      setLoading(false);
    }
  }, [keyId, failuresOnly]);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const failed = rows.filter((r) => r.status >= 400).length;

  async function toggleRow(id: number) {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await superApi.apiRequestLog(id));
    } catch {
      toast('שגיאה בטעינת פרטי הבקשה', 'danger');
      setOpenId(null);
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <Card
      title={`${RECENT_REQUESTS_LIMIT} בקשות אחרונות ל-API${keyId ? ` — ${keyLabel ?? `מפתח #${keyId}`}` : ''}`}
      action={(
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {keyId && (
            <button type="button" className="btn btn-sm" onClick={onClearKey}>
              <Icon name="close" size={13} /> כל המפתחות
            </button>
          )}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={failuresOnly} onChange={(e) => setFailuresOnly(e.target.checked)} />
            רק כשלים
          </label>
          <button type="button" className="btn btn-sm" onClick={() => void load()} disabled={loading}>
            <Icon name="history" size={13} /> רענן
          </button>
        </div>
      )}
    >
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 8 }}>
        {loading && rows.length === 0 ? 'טוען…' : `${rows.length} מוצגות מתוך ${total} · ${failed} כשלים ברשימה · מתרענן כל 15 שניות`}
      </div>
      {rows.length === 0 && !loading ? (
        <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', padding: '8px 0' }}>
          {failuresOnly ? 'אין כשלים — כל הבקשות האחרונות הצליחו.' : 'אין עדיין קריאות API מתועדות.'}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['זמן', 'לקוח', 'מפתח', 'בקשה', 'סטטוס', 'משך', 'IP', 'סיבה'].map((h) => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'start', fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const bad = r.status >= 400;
                const redirect = r.status >= 300 && r.status < 400;
                const open = openId === r.id;
                return [
                  <tr
                    key={r.id}
                    data-testid="api-log-row"
                    data-status={r.status}
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    title="לחיצה: איך הבקשה הגיעה ולמה נכשלה"
                    onClick={() => void toggleRow(r.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void toggleRow(r.id); } }}
                    style={{ borderTop: '1px solid var(--line)', background: open ? 'var(--surface-2)' : bad ? 'var(--danger-soft, #FDF2F1)' : undefined, cursor: 'pointer' }}
                  >
                    <td style={cell}><span dir="ltr" style={{ fontSize: 'var(--t-xs)', whiteSpace: 'nowrap' }}>{fmtTime(r.created_at)}</span></td>
                    <td style={cell}>{r.tenant_name ?? '—'}</td>
                    <td style={cell}>{r.key_name ?? '—'}</td>
                    <td style={cell}><code dir="ltr" style={{ fontSize: 'var(--t-xs)' }}>{r.method} {r.path}</code></td>
                    <td style={cell}>
                      <StatusBadge tone={bad ? 'danger' : redirect ? 'warn' : 'ok'} size="sm">{r.status}</StatusBadge>
                    </td>
                    <td style={cell}>{r.duration_ms !== null ? <span dir="ltr">{r.duration_ms}ms</span> : '—'}</td>
                    <td style={cell}><span dir="ltr" style={{ fontSize: 'var(--t-xs)' }}>{r.ip ?? '—'}</span></td>
                    <td style={cell}>
                      {r.error
                        ? <code dir="ltr" style={{ fontSize: 'var(--t-xs)', color: bad || redirect ? 'var(--danger)' : 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{r.error}</code>
                        : '—'}
                    </td>
                  </tr>,
                  open && (
                    <tr key={`${r.id}-detail`} data-testid="api-log-detail">
                      <td colSpan={8} style={{ padding: 0, borderTop: '1px solid var(--line)' }}>
                        {detailLoading || !detail
                          ? <div style={{ padding: 12, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען את הבקשה…</div>
                          : <RequestDetail d={detail} />}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const cell: React.CSSProperties = { padding: '8px 12px', fontSize: 'var(--t-sm)', verticalAlign: 'middle' };

const pretty = (v: unknown) => (v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v, null, 2));

/** שחזור הבקשה כ-curl — כדי לשלוח ללקוח "ככה זה הגיע אלינו" או להריץ שוב. */
export function toCurl(d: ApiRequestLogDetail): string {
  const base = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/api\/?$/, '') || 'https://api.kailai.tech';
  const qs = d.query && Object.keys(d.query).length ? '?' + new URLSearchParams(d.query as Record<string, string>).toString() : '';
  const parts = [`curl -X ${d.method} '${base}${d.path}${qs}'`];
  const skip = new Set(['host', 'content-length', 'connection', 'accept-encoding']);
  for (const [k, v] of Object.entries(d.headers ?? {})) {
    if (skip.has(k.toLowerCase())) continue;
    parts.push(`-H '${k}: ${String(v).replace(/'/g, "'\\''")}'`);
  }
  if (d.body !== null && d.body !== undefined && !(typeof d.body === 'object' && Object.keys(d.body as object).length === 0)) {
    parts.push(`--data '${JSON.stringify(d.body).replace(/'/g, "'\\''")}'`);
  }
  return parts.join(' \\\n  ');
}

function RequestDetail({ d }: { d: ApiRequestLogDetail }) {
  const diag = d.diagnosis;
  const copy = (text: string, label: string) => {
    void navigator.clipboard?.writeText(text).then(() => toast(`${label} הועתק`, 'ok')).catch(() => {});
  };
  return (
    <div style={{ padding: 12, display: 'grid', gap: 12 }}>
      {/* האבחון קודם — זו השאלה שבגללה לחצו. */}
      <div
        data-testid="api-log-diagnosis"
        style={{
          padding: '10px 12px', borderRadius: 'var(--r-2)',
          background: diag.ok ? 'var(--ok-soft, #E7F6EE)' : 'var(--danger-soft, #FDF2F1)',
          border: `1px solid ${diag.ok ? 'var(--ok, #1F7A50)' : 'var(--danger, #B4322C)'}`,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: diag.lines.length ? 6 : 0 }}>{diag.title}</div>
        {diag.lines.length > 0 && (
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 'var(--t-sm)', display: 'grid', gap: 2 }}>
            {diag.lines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <Section title="מי ואיך">
          <dl style={dl}>
            <dt>לקוח</dt><dd>{d.tenant_name ?? '—'}</dd>
            <dt>מפתח</dt><dd>{d.key_name ?? '—'}{d.key_prefix ? <code dir="ltr" style={{ marginInlineStart: 6, fontSize: 'var(--t-xs)' }}>{d.key_prefix}…</code> : null}</dd>
            <dt>הרשאות המפתח</dt><dd>{d.key_scopes?.length ? d.key_scopes.join(', ') : 'גישה מלאה'}</dd>
            <dt>מקור (IP)</dt><dd dir="ltr">{d.ip ?? '—'}</dd>
            <dt>זמן</dt><dd dir="ltr">{new Date(d.created_at).toLocaleString('he-IL')}</dd>
            <dt>משך</dt><dd dir="ltr">{d.duration_ms !== null ? `${d.duration_ms}ms` : '—'}</dd>
          </dl>
        </Section>
        <Section title="כותרות שנשלחו" action={<CopyBtn onClick={() => copy(pretty(d.headers), 'הכותרות')} />}>
          {d.headers && Object.keys(d.headers).length
            ? <dl style={dl}>{Object.entries(d.headers).map(([k, v]) => [<dt key={k + 'k'} dir="ltr">{k}</dt>, <dd key={k + 'v'} dir="ltr" style={{ wordBreak: 'break-all' }}>{v}</dd>])}</dl>
            : <span style={{ color: 'var(--muted)' }}>לא נשמרו</span>}
        </Section>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <Section title="גוף הבקשה (כפי שהגיע)" action={<CopyBtn onClick={() => copy(pretty(d.body), 'הגוף')} />}>
          <Pre>{pretty(d.body)}</Pre>
        </Section>
        <Section title={`תשובת השרת (${d.status})`} action={<CopyBtn onClick={() => copy(pretty(d.response_body), 'התשובה')} />}>
          <Pre>{pretty(d.response_body)}</Pre>
        </Section>
      </div>

      {d.query && Object.keys(d.query).length > 0 && (
        <Section title="Query string"><Pre>{pretty(d.query)}</Pre></Section>
      )}

      <Section title="שחזור כ-curl" action={<CopyBtn onClick={() => copy(toCurl(d), 'פקודת curl')} />}>
        <Pre>{toCurl(d)}</Pre>
      </Section>
    </div>
  );
}

const dl: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', margin: 0, fontSize: 'var(--t-sm)' };

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>{title}</strong>
        {action}
      </div>
      {children}
    </div>
  );
}

function CopyBtn({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn ghost btn-sm" onClick={(e) => { e.stopPropagation(); onClick(); }} title="העתק">
      <Icon name="copy" size={13} />
    </button>
  );
}

function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre dir="ltr" style={{ margin: 0, fontSize: 'var(--t-xs)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto', background: 'var(--surface-2)', padding: 8, borderRadius: 'var(--r-2)' }}>
      {children}
    </pre>
  );
}
